import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Spade, Users, Trophy, Coins, Check, BellRing, Sparkles, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FEATURES = [
    {
        icon: Spade,
        title: 'Real-Time Texas Hold\'em',
        desc: 'Cash games + sit-and-gos + scheduled tournaments. 6-max & 9-max tables.',
    },
    {
        icon: Coins,
        title: 'Your Credits, Your Chips',
        desc: 'Walk into the room and your Betohalics balance instantly converts to poker chips. Walk out and chips convert back to credits.',
    },
    {
        icon: Users,
        title: 'Private Club Room',
        desc: "Members-only access. No randos. Just our community at the table.",
    },
    {
        icon: Trophy,
        title: 'Pots Still Build',
        desc: "Every poker buy-in feeds the Community Jackpots — same as every other wager you make.",
    },
];

const PokerComingSoon = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState(null); // {joined, joined_at, total}
    const [contact, setContact] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/poker/waitlist/status`, { withCredentials: true });
                setStatus(data);
                if (data.contact) setContact(data.contact);
            } catch { /* silent */ }
        })();
    }, [user]);

    const join = async () => {
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/poker/waitlist`,
                { contact: contact.trim() || undefined },
                { withCredentials: true });
            setStatus({ joined: true, joined_at: new Date().toISOString(), contact, total: data.total });
            toast.success("You're on the list — we'll ping you the moment poker goes live");
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to join waitlist');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a]">
            <Header />
            <div className="max-w-4xl mx-auto px-4 py-6">
                <button
                    onClick={() => navigate('/lobby')}
                    className="flex items-center gap-1.5 text-[11px] text-[#A0AAB2] hover:text-white mb-4 uppercase tracking-wider font-bold transition-colors"
                    data-testid="poker-back-btn"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to Casino Floor
                </button>

                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden rounded-2xl border-2 border-[#39FF14]/30 bg-gradient-to-br from-[#39FF14]/10 via-black/60 to-[#FF007F]/10 p-6 sm:p-10 mb-6"
                    data-testid="poker-hero"
                >
                    <div
                        className="absolute -top-32 -right-32 w-72 h-72 rounded-full pointer-events-none"
                        style={{ background: 'radial-gradient(circle, rgba(57,255,20,0.18), transparent 70%)' }}
                    />
                    <div className="relative">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/40 text-[10px] uppercase tracking-[0.25em] font-black text-[#39FF14] mb-3">
                            <Sparkles className="w-3 h-3" /> Coming Soon
                        </span>
                        <h1 className="text-3xl sm:text-5xl font-black text-white leading-tight">
                            The <span className="text-[#39FF14]">Poker Room</span> is being built.
                        </h1>
                        <p className="text-[#A0AAB2] text-sm sm:text-base mt-3 max-w-xl">
                            Private members-only club. Real-time tables. Your Betohalics credits convert directly to chips.
                            Every buy-in keeps building the community jackpot pots — same as every other wager you make.
                        </p>
                    </div>
                </motion.div>

                {/* Features */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + i * 0.08 }}
                            className="card-dark p-4 flex gap-3"
                            data-testid={`poker-feature-${i}`}
                        >
                            <div className="w-10 h-10 shrink-0 rounded-xl bg-[#39FF14]/12 border border-[#39FF14]/30 flex items-center justify-center">
                                <f.icon className="w-5 h-5 text-[#39FF14]" />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-sm mb-0.5">{f.title}</h3>
                                <p className="text-[#A0AAB2] text-xs leading-relaxed">{f.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Waitlist / status card */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45 }}
                    className="card-dark p-5 sm:p-6"
                    data-testid="poker-waitlist-card"
                >
                    {status?.joined ? (
                        <div className="flex items-start gap-3" data-testid="poker-waitlist-joined">
                            <div className="w-10 h-10 shrink-0 rounded-full bg-[#39FF14]/15 border-2 border-[#39FF14] flex items-center justify-center">
                                <Check className="w-5 h-5 text-[#39FF14]" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-white font-bold text-base">You're on the list ✨</h3>
                                <p className="text-[#A0AAB2] text-sm mt-0.5">
                                    We'll notify you the moment the room opens. {status.total > 0 && (
                                        <span>You're #<span className="font-mono font-bold text-[#FFD700]">{status.total}</span> on the waitlist.</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div data-testid="poker-waitlist-form">
                            <div className="flex items-center gap-2 mb-3">
                                <BellRing className="w-5 h-5 text-[#FFD700]" />
                                <h3 className="text-white font-bold text-base">Get notified when poker launches</h3>
                            </div>
                            <p className="text-[#A0AAB2] text-sm mb-4">
                                Drop an email or phone (optional — we already have your account info) and you'll be among the first invited when tables open up.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Input
                                    value={contact}
                                    onChange={(e) => setContact(e.target.value)}
                                    placeholder="email or phone (optional)"
                                    className="flex-1 bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="poker-waitlist-input"
                                />
                                <Button
                                    onClick={join}
                                    disabled={submitting}
                                    className="bg-[#39FF14] text-black font-black hover:bg-[#39FF14]/90 uppercase tracking-wider px-6"
                                    data-testid="poker-waitlist-submit"
                                >
                                    {submitting ? 'Joining…' : 'Notify Me'}
                                </Button>
                            </div>
                            {status?.total > 0 && (
                                <p className="text-[#A0AAB2] text-[11px] mt-3 flex items-center gap-1.5">
                                    <Users className="w-3 h-3 text-[#39FF14]" />
                                    <span className="font-mono font-bold text-[#39FF14]">{status.total}</span> player{status.total === 1 ? '' : 's'} already waiting
                                </p>
                            )}
                        </div>
                    )}
                </motion.div>

                <p className="text-[10px] text-[#A0AAB2]/60 text-center mt-6">
                    Powered by X-Poker — premium club-based poker network. Members-only.
                </p>
            </div>
        </div>
    );
};

export default PokerComingSoon;
